import React, { useState } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  Button,
  Link,
  Chip,
  ModalFooter,
} from '@nextui-org/react';

import { Tasker } from 'common/src/utils/types';

interface ModalViewTaskerProps {
  isOpen: boolean;
  onApprove: (tasker: Tasker) => Promise<void>;
  onClose: () => void;
  tasker: Tasker | null;
  taskerCategories: string[];
}

const ModalViewTasker: React.FC<ModalViewTaskerProps> = ({
  isOpen,
  onApprove,
  onClose,
  tasker,
  taskerCategories,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  if (!tasker) return null;

  const handleApprove = async () => {
    setIsLoading(true);
    try {
      await onApprove(tasker);
      onClose();
    } catch (error) {
      console.error('Error approving tasker:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // we had a bug that the url was being saved with %0A
  // and this is a hacky fix,
  // so in the future we should actually normalize the urls probably
  const cleanUrl = (url: string | null) => {
    if (url && url.includes('%0A')) {
      return url.replace(/%0A/g, '');
    }
    return url;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <span>Información del Tasker</span>
        </ModalHeader>
        <ModalBody className="h-[60vh] overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p>
                <strong>Nombre:</strong> {tasker.name} {tasker.last_name}
              </p>
              <p>
                <strong>Email:</strong> {tasker.email}
              </p>
              <p>
                <strong>Teléfono:</strong> {tasker.phone_number}
              </p>
              <p>
                <strong>Universidad:</strong> {tasker.university}
              </p>
              <p>
                <strong>Carrera:</strong> {tasker.carreer}
              </p>
              <p>
                <strong>Año universitario:</strong> {tasker.university_year}
              </p>
              <p>
                <strong>Aprobado:</strong> {tasker.is_approved ? 'Sí' : 'No'}
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Categorías:</h4>
              <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                {taskerCategories.map((category, index) => (
                  <Chip key={index} size="sm" className="bg-blue-100 text-blue-800">
                    {category}
                  </Chip>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-4">
            <h4 className="font-semibold mb-2">Certificados:</h4>
            <ul className="list-disc list-inside">
              {tasker.certificate_regular_alumn_url && (
                <li>
                  <Link
                    href={cleanUrl(tasker.certificate_regular_alumn_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Certificado de Alumno Regular
                  </Link>
                </li>
              )}
              {tasker.photo_selfie_url && (
                <li>
                  <Link
                    href={cleanUrl(tasker.photo_selfie_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Foto Selfie
                  </Link>
                </li>
              )}
              {tasker.certificate_criminal_record_url && (
                <li>
                  <Link
                    href={cleanUrl(tasker.certificate_criminal_record_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Certificado de Antecedentes
                  </Link>
                </li>
              )}
            </ul>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="flex justify-end gap-2 w-full">
            <Button
              color="danger"
              variant="light"
              onPress={onClose}
              className="text-purple-500 border border-purple-500"
            >
              Cerrar
            </Button>
            {!tasker.is_approved && (
              <Button
                className="bg-purple-500 text-white"
                color="primary"
                isDisabled={isLoading}
                onPress={handleApprove}
              >
                Aprobar
              </Button>
            )}
          </div>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalViewTasker;
